import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  Typography,
  FormControl,
  SelectChangeEvent,
  Alert,
  Snackbar,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
} from '@mui/material';

import { User } from '../../types/auth';
import { AutoApprovalDomain, getUsers, updateUserRole, getAutoApprovalDomains, addAutoApprovalDomain, deleteAutoApprovalDomain, deleteUser, approveUser } from '../../api/admin';

export const UsersPage: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [domains, setDomains] = useState<AutoApprovalDomain[]>([]);
  const [newDomain, setNewDomain] = useState('');
  const [showDomainDialog, setShowDomainDialog] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{open: boolean; message: string; severity: 'success' | 'error'}>({ 
    open: false, 
    message: '', 
    severity: 'success' 
  });

  const fetchUsers = async () => {
    try {
      const data = await getUsers();
      setUsers(data);
      setError(null);
    } catch (err: any) {
      const errorMessage = err?.message || 'Failed to load users';
      console.error('Error fetching users:', err);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const fetchDomains = async () => {
    try {
      const data = await getAutoApprovalDomains();
      setDomains(data);
    } catch (error) {
      console.error('Error fetching domains:', error);
    }
  };

  const handleAddDomain = async () => {
    try {
      await addAutoApprovalDomain(newDomain);
      setNewDomain('');
      setShowDomainDialog(false);
      fetchDomains();
      setSnackbar({
        open: true,
        message: 'Domain added successfully',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error adding domain:', error);
      setSnackbar({
        open: true,
        message: 'Failed to add domain',
        severity: 'error'
      });
    }
  };

  const handleDeleteDomain = async (id: number) => {
    try {
      await deleteAutoApprovalDomain(id);
      fetchDomains();
      setSnackbar({
        open: true,
        message: 'Domain deleted successfully',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error deleting domain:', error);
      setSnackbar({
        open: true,
        message: 'Failed to delete domain',
        severity: 'error'
      });
    }
  };

  const handleDeleteUser = async (userId: string) => {
    if (!window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      return;
    }

    try {
      await deleteUser(userId);
      setUsers(users.filter(user => user.id !== userId));
      setSnackbar({
        open: true,
        message: 'User deleted successfully',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error deleting user:', error);
      setSnackbar({
        open: true,
        message: 'Failed to delete user',
        severity: 'error'
      });
    }
  };

  const handleApproveUser = async (userId: string) => {
    try {
      await approveUser(userId);
      setUsers(users.map(user => 
        user.id === userId ? { ...user, is_approved: true } : user
      ));
      setSnackbar({
        open: true,
        message: 'User approved successfully',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error approving user:', error);
      setSnackbar({
        open: true,
        message: 'Failed to approve user',
        severity: 'error'
      });
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchDomains();
  }, []);

  const handleRoleChange = async (userId: string, newRole: 'ADMIN' | 'USER') => {
    try {
      await updateUserRole(userId, newRole);
      
      // Update the local state
      setUsers(users.map(user => 
        user.id === userId ? { ...user, role: newRole } : user
      ));
      
      setSnackbar({
        open: true,
        message: 'User role updated successfully',
        severity: 'success'
      });
    } catch (err) {
      console.error('Error updating user role:', err);
      setSnackbar({
        open: true,
        message: 'Failed to update user role',
        severity: 'error'
      });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  if (loading) {
    return <Typography>Loading users...</Typography>;
  }

  if (error) {
    return <Typography color="error">{error}</Typography>;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          User Management
        </Typography>
        <Button variant="contained" onClick={() => setShowDomainDialog(true)}>
          Manage Auto-Approval Domains
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell width="25%">Email</TableCell>
              <TableCell width="15%">Role</TableCell>
              <TableCell width="15%">Status</TableCell>
              <TableCell width="15%">Created At</TableCell>
              <TableCell width="15%">Last Login</TableCell>
              <TableCell width="15%">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((user) => (
              <TableRow key={user.id}>
                <TableCell>{user.email}</TableCell>
                <TableCell>
                  <FormControl size="small" fullWidth>
                    <Select
                      value={user.role}
                      onChange={(e: SelectChangeEvent) => 
                        handleRoleChange(user.id, e.target.value as 'ADMIN' | 'USER')
                      }
                    >
                      <MenuItem value="USER">User</MenuItem>
                      <MenuItem value="ADMIN">Admin</MenuItem>
                    </Select>
                  </FormControl>
                </TableCell>
                <TableCell>
                  <Chip 
                    label={user.is_approved ? 'Approved' : 'Pending'}
                    color={user.is_approved ? 'success' : 'warning'}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  {new Date(user.createdAt).toLocaleDateString()}
                </TableCell>
                <TableCell>
                  {user.lastLogin ? new Date(user.lastLogin).toLocaleDateString() : 'Never'}
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    {!user.is_approved && (
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => handleApproveUser(user.id)}
                        color="success"
                      >
                        Approve
                      </Button>
                    )}
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => handleDeleteUser(user.id)}
                      color="error"
                    >
                      Remove
                    </Button>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={showDomainDialog} onClose={() => setShowDomainDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Auto-Approval Domains</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Users registering with these email domains will be automatically approved.
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <TextField
              fullWidth
              label="New Domain"
              value={newDomain}
              onChange={(e) => setNewDomain(e.target.value)}
              placeholder="example.com"
              size="small"
            />
            <Button variant="contained" onClick={handleAddDomain}>
              Add
            </Button>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Domain</TableCell>
                  <TableCell>Added</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {domains.map((domain) => (
                  <TableRow key={domain.id}>
                    <TableCell>{domain.domain}</TableCell>
                    <TableCell>{new Date(domain.created_at).toLocaleString()}</TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        color="error"
                        onClick={() => handleDeleteDomain(domain.id)}
                      >
                        Delete
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDomainDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};
